import React, {useEffect, useState, useCallback, useMemo} from "react";
import {useDispatch} from "react-redux";
import {useAppSelector} from "../../app/store";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {getAdminQuestion, QuestionDataParams} from "../../app/slices/adminQuestion";
import AdminLoader from "./AdminLoader";
import {Button, Col, Collapse, Container, Dropdown, Row} from "react-bootstrap";
import QuestionBasicDetails from "./questionView/QuestionBasicDetails";
import {DataParams} from "../../types/admin";
import {ADMIN_LANG} from "../../settings";
import {LanguageFilter, TeamFilter} from "./lists/ListFilters";
import SelectChart from "./questionView/SelectChart";
import OrderChart from "./questionView/OrderChart";
import ConnectChart from "./questionView/ConnectChart";
import ConnectTable from "./questionView/ConnectTable";
import SelectTable from "./questionView/SelectTable";
import OrderTable from "./questionView/OrderTable";
import OpenTable from "./questionView/OpenTable";
import ErrorPage from "./ErrorPage";
import {getVersionDetails, getVersionIcon} from "./GameView";
import {ChevronDown} from "react-bootstrap-icons";
import NumberScaleChart from "./questionView/NumberScaleChart";
import NumberScaleTable from "./questionView/NumberScaleTable";
import NumberScaleNpsTrendChart from "./questionView/NumberScaleNpsTrendChart";

type Props = {
    params: DataParams;
};

const QuestionView: React.FC<Props> = ({params}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {
        loaded,
        error,
        type,
        select_data,
        connect_data,
        order_data,
        text_data,
        current_version_id,
        versions,
        max_points,
        number_scale_data,
        nps_data
    } = useAppSelector(state => state.adminQuestion);
    const {question_id, version_id} = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [filtersOpen, setFiltersOpen] = useState(false);


    useEffect(() => {
        if (question_id) {
            const payload: QuestionDataParams = { question_id, ...params };
            if (version_id) {
                payload.version_id = version_id;
            }

            dispatch(getAdminQuestion(payload));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [question_id, version_id, JSON.stringify(params)]);

    const clearFilters = useCallback(() => {
        setSearchParams([]);
    }, [setSearchParams]);

    const filtersActive = useMemo(() => searchParams.size > 0, [searchParams]);

    const handleVersionSelect = (version_id: string) => {
        navigate(`/admin/questions/${question_id}/versions/${version_id}`);
    }

    if (error) {
        return (
            <ErrorPage message={error}/>
        )
    }

    const renderChartAndTable = () => {
        switch (type) {
            case "select":
                return (
                    <>
                        <SelectChart optionsData={select_data}/>
                        <SelectTable maxPoints={max_points} optionsData={select_data}/>
                    </>
                );
            case "connect":
                return (
                    <>
                        <ConnectChart optionsData={connect_data}/>
                        <ConnectTable optionsData={connect_data}/>
                    </>
                );
            case "order":
                return (
                    <>
                        <OrderChart optionsData={order_data}/>
                        <OrderTable optionsData={order_data}/>
                    </>
                );
            case "text":
                return <OpenTable optionsData={text_data}/>;
            case "number_scale":
                return (
                    <>
                    <NumberScaleChart
                        optionsData={number_scale_data}
                        npsScale={nps_data}
                    />
                    <NumberScaleTable optionsData={number_scale_data} npsScale={nps_data} />
                    <NumberScaleNpsTrendChart
                        npsScale={nps_data}
                        dateStart={params.date_start || params.start}
                        dateEnd={params.date_end || params.end}
                    />
                    </>
                )
            default:
                return null;
        }
    };

    const currentVersion = versions.find(version => version.version_id === current_version_id);

    return (
        <Container className='pt-4'>
            {!loaded && <AdminLoader/>}
            <Row className='pb-3'>
                <Col>
                    <Button
                        onClick={() => setFiltersOpen(!filtersOpen)}
                        aria-controls='extra-filters'
                        aria-expanded={filtersOpen}
                    >
                        {ADMIN_LANG.filter}
                    </Button>
                    {filtersActive && (
                        <Button onClick={clearFilters} variant='outline-primary' className='ms-2'>
                            {ADMIN_LANG.clear_filters}
                        </Button>
                    )}
                </Col>
                <Col className='text-end'>
                    {currentVersion && (
                    <Dropdown>
                        <Dropdown.Toggle variant='outline-primary'>
                            {getVersionIcon(currentVersion.is_published, currentVersion.is_draft, currentVersion.is_archived)} {ADMIN_LANG.version} #{currentVersion.number}
                            <ChevronDown size='1.5rem' className="ms-2" />
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            {versions
                                .filter(version => version.version_id !== version_id)
                                .map((version) => (
                                    <Dropdown.Item
                                        key={version.version_id}
                                        onClick={() => handleVersionSelect(version.version_id)}
                                        className={getVersionDetails(version.is_published, version.is_draft, version.is_archived).className}
                                    >
                                        {getVersionIcon(version.is_published, version.is_draft, version.is_archived)} {ADMIN_LANG.version} #{version.number}
                                    </Dropdown.Item>
                                ))}
                        </Dropdown.Menu>
                    </Dropdown>
                    )}
                </Col>
            </Row>
            <Row>
                <Col xs={12}>
                    <Collapse in={filtersOpen}>
                        <Row>
                            <LanguageFilter/>
                            <TeamFilter/>
                            <Col xs={12} className="pb-3"></Col>
                        </Row>
                    </Collapse>
                </Col>
            </Row>
            <Row className='gy-4 pb-4'>
                <QuestionBasicDetails/>
                {renderChartAndTable()}
            </Row>
        </Container>
    );
};

export default React.memo(QuestionView);
